// queries/useEnumHelpers.ts
import { Area } from '@/stores/SubprojectStore';
import {
  AreaEnum,
  CSIBuildingUsage,
  CSIEnum,
  EnergySource,
  convertArea,
  useEnums as useEnumsQuery,
} from './useEnums';

// Define the return type of useEnums
interface UseEnumsResult {
  sectors: CSIEnum[];
  technologyCategoriesAll: CSIEnum[];
  technologyCategoriesBuildings: CSIEnum[];
  buildingUsages: CSIBuildingUsage[];
  areaUnits: AreaEnum[];
  energySources: EnergySource[];
}

// Define the shape of the data object for better typing
interface EnumsData {
  sectors: CSIEnum[];
  technologyCategoriesAll: CSIEnum[];
  technologyCategoriesBuildings: CSIEnum[];
  buildingUsages: CSIBuildingUsage[];
  areaUnits: AreaEnum[];
  energySources: EnergySource[];
}

// Define a type for the return value when no selector is provided
interface EnumHelperReturnType extends EnumsData {
  isLoading: boolean;
  error: Error | null;
  convertArea: (area: Area) => Area[];
  // getDisplayName: (enumType: keyof EnumsData, value: string) => string;
  isReady: boolean;
}

// Type for the selector function
type SelectorFn<T> = (data: EnumsData) => T;

// This hook provides a more convenient API for components to use enums
export function useEnumHelpers(): EnumHelperReturnType;
export function useEnumHelpers<T>(selector: SelectorFn<T>): T;
export function useEnumHelpers<T>(
  selector?: SelectorFn<T>,
): T | EnumHelperReturnType {
  // Use type assertion to ensure TypeScript understands the shape of the data
  const enumsResult = useEnumsQuery() as unknown as UseEnumsResult;

  // Safely access and validate the data with defaults
  const defaultData: EnumsData = {
    sectors: Array.isArray(enumsResult?.sectors) ? enumsResult.sectors : [],
    technologyCategoriesAll: Array.isArray(enumsResult?.technologyCategoriesAll)
      ? enumsResult.technologyCategoriesAll
      : [],
    technologyCategoriesBuildings: Array.isArray(
      enumsResult?.technologyCategoriesBuildings,
    )
      ? enumsResult.technologyCategoriesBuildings
      : [],
    buildingUsages: Array.isArray(enumsResult?.buildingUsages)
      ? enumsResult.buildingUsages
      : [],
    areaUnits: Array.isArray(enumsResult?.areaUnits)
      ? enumsResult.areaUnits
      : [],
    energySources: Array.isArray(enumsResult?.energySources)
      ? enumsResult.energySources
      : [],
  };

  // Destructure for easier access
  const { areaUnits } = defaultData;
  const isLoading = false; // Since useEnums doesn't expose loading state
  const error = null; // Since useEnums doesn't expose error state

  // If a selector is provided, use it to extract specific data
  if (selector) {
    return selector(defaultData);
  }

  // Otherwise return the full data object with utility functions
  return {
    isLoading,
    error,
    ...defaultData,

    // Utility functions
    convertArea: (area: Area): Area[] => {
      if (!areaUnits || areaUnits.length === 0) return [area]; // Return original if units not loaded
      return convertArea(area, areaUnits);
    },

    // Helper to get the display name for a value
    // Commented out as it doesn't seem to be used anywhere in the repository & it was raising errors
    // since AreaEnum no longer has a value field
    // getDisplayName: (enumType: keyof EnumsData, value: string): string => {
    //   const enumArray = defaultData[enumType];
    //   if (!enumArray) return value;

    //   const found = Array.isArray(enumArray)
    //     ? enumArray.find((item: CSIEnum) => item.name === value || item.value === value)
    //     : null;
    //   return found ? ('description' in found && found.description ? found.description : found.value || found.name) : value;
    // },

    // Helper to check if enums are loaded
    isReady: Object.values(defaultData).every(
      (array) => Array.isArray(array) && array.length > 0,
    ),
    // Spread the default data last to ensure it can be overridden by the utility functions
    ...defaultData,
  };
}
